import { mapActions, mapGetters } from 'vuex'
import countries from '@/utils/_countries.json'
import languages from '@/utils/_languages.json'
import SaaSCTA from '@/components/SaaSCTA/SaaSCTA.vue'
import keywordExclusionRegex from '@/utils/keywordExclusionRegex'
export default {
  name: 'PaidClusterer',
  data () {
    return {
      isLoading: {
        clusterer: false,
        contentplans: false,
        checkout: false,
        recluster: false
      },

      keywordLimit: 1000,
      keywordLimitPaid: 20000,

      keywords: null,
      keywordRules: [
        value => !value || value?.split(/[\r\n\t]+/)?.length <= this.keywordLimit || `Enter less than ${this.keywordLimit + 1} keywords`,
        value => !value || value?.split(/[\r\n\t]+/)?.length > 1 || 'Enter at least 2 keywords'
      ],
      domain: null,
      domainRules: [
        // value can be empty
        value => !value || /^(https?:\/\/)?(www\.)?([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.)+[a-zA-Z]{2,}\/?$/.test(value) || 'Invalid domain'
      ],

      form: null,
      countries,
      languages,
      country: 'USA',
      language: 'en',
      serpOverlap: 2,

      interval: null,
      tableHeaders: [
        { text: 'Domain', value: 'domain' },
        { text: 'Country', value: 'country_crawl' },
        { text: 'Language', value: 'language' },
        { text: 'SERP overlap', value: 'minimum_score' },
        { text: 'Spreadsheet link', value: 'csv_url', sortable: false },
        { text: 'Created', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      reclusterContentplanId: null,
      reclusterOverlap: 2,
      reclusterShowDialog: false
    }
  },
  computed: {
    ...mapGetters([
      'activeWorkspace',
      'contentplans',
      'websocketMessages'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    keywordsHint () {
      const keywordsArray = this.keywords?.split(/[\r\n\t]+/)?.filter(k => k)
      if (!keywordsArray?.length) return 'Enter a list of keywords, one per line'
      if (/,+/u.test(this.keywords)) return 'Enter one keyword per line. Don\'t use commas to seperate keywords'
      if (keywordsArray.some(k => new RegExp(keywordExclusionRegex.regex).test(k))) return 'Keywords with punctuation or special symbols will be ignored'
      return ''
    },
    isRusLocation () {
      return this.country === 'RUS' || this.country === 'BLR'
    },
    isRusDomain () {
      if (!this.form) return false
      return /(ru|by)\/?$/u.test(this.domain)
    },
    hasCleanKeywords () {
      const keywordsArray = this.keywords?.split(/[\r\n\t]+/)?.filter(k => k)
      if (!keywordsArray?.length) return true
      const keywordsCleaned = keywordsArray?.map(k => k.trim().toLowerCase()).filter(k => !!k).filter(k => {
        return !new RegExp(keywordExclusionRegex.regex).test(k)
      })

      return keywordsCleaned && keywordsCleaned.length > 0
    },
    errorMessage () {
      if (this.isRusLocation) return 'We don\'t support Russia or Belarus.'
      if (!this.hasCleanKeywords) return 'Please enter keywords without punctuation or special symbols'
      if (this.isRusDomain) return 'We don\'t support Russian or Belarussian domains.'
      if (this.countKeywords() > this.keywordLimit) return `You can cluster up to ${this.keywordLimit} keywords at a time.`
      if (this.countKeywords() > this.activeWorkspace?.remaining_monthly_credits) return 'You don\'t have enough monthly credits left.'
      return null
    },
    contentplanHistory () {
      if (!this.contentplans?.length) return []
      return this.contentplans.filter(cp => cp.name === 'Free clustering plan').sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },
    hasActiveSubscription () {
      if (!this.activeWorkspace) return false
      const activeSubscription = this.activeWorkspace.type === 'saas' && this.activeWorkspace.subscription_active
      if (activeSubscription) this.keywordLimit = this.keywordLimitPaid
      return activeSubscription
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    },
    websocketMessages: {
      deep: true,
      handler (messages) {
        if (!messages?.length) return
        const lastMessage = messages[messages.length - 1]
        if (lastMessage?.messages?.[0]) {
          this.handleWebsocketMessage(lastMessage.messages[0])
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'contentplanGetList',
      'sendWebsocketMessage',
      'runClustererHttp',
      'createStripeCheckoutSession',
      'contentplanPut'
    ]),
    async loadData () {
      this.isLoading.contentplans = true
      try {
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
      } catch (e) {
        console.error(e)
      }

      this.isLoading.contentplans = false
    },
    countKeywords () {
      if (!this.keywords?.length) return 0
      // count keywords one per line
      return this.keywords?.split(/[\r\n\t]+/)?.filter(k => k).length || 0
    },
    async runClusterer () {
      try {
        if (!this.keywords?.length) return
        this.isLoading.clusterer = true

        // Turn this.keywords string into array
        const keywords = this.keywords.split(/[\r\n\t]+/)?.filter(k => k?.length)

        // Trigger clustering via HTTP POST (avoids 128KB WebSocket limit)
        // Progress updates still come via WebSocket (backend looks up connectionId from userId)
        await this.runClustererHttp({
          keywords,
          country: this.country,
          language: this.language,
          serpOverlap: this.serpOverlap,
          domain: this.domain,
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
      } catch (err) {
        console.log(err)
        this.isLoading.clusterer = false
      }
    },
    async handleWebsocketMessage (messageData) {
      const { stepName, status, csvUrl } = messageData

      console.log('WebSocket message:', { stepName, status, csvUrl })

      // Handle step transitions from state machine
      if (stepName === 'starting' && status === 'started') {
        // Clustering has started, stop loading button and load first contentplan
        this.isLoading.clusterer = false
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
      }

      if (stepName === 'Crawling' && status === 'done') {
        // Crawling complete, moving to grouping
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
      }

      if (stepName === 'Clustering' && status === 'done') {
        // Clustering complete
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
      }

      if (stepName === 'CreateCSV' && status === 'done' && csvUrl) {
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
        this.isLoading.clusterer = false
        this.isLoading.recluster = false
        this.reset()
      }

      // Handle errors
      if (status === 'error') {
        this.isLoading.clusterer = false
        this.isLoading.recluster = false
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
      }
    },
    reset () {
      clearInterval(this.interval)
      this.interval = null
      this.keywords = null
      this.country = 'USA'
      this.language = 'en'
    },
    async clustererStatus () {
      await this.contentplanGetList({
        workspaceId: this.workspaceId,
        type: 'saas-cluster'
      })
      // if all contentplans are done, stop interval
      if (this.contentplans.every(cp => cp.labelling_ended && cp.csv_url)) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
    async redirectToCheckout () {
      this.isLoading.checkout = true
      const response = await this.createStripeCheckoutSession({
        workspaceId: this.workspaceId
      })
      const redirectURL = response?.url
      this.isLoading.checkout = false
      // redirect to stripe checkout
      if (redirectURL) {
        window.location.href = redirectURL
      }
    },
    async reclusterContentplan () {
      const contentplanId = this.reclusterContentplanId
      this.isLoading.recluster = true
      try {
        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId,
          action: 'update',
          minimum_score: this.reclusterOverlap
        })
        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId,
          action: 'resetClusters',
          recluster: true
        })
        await this.contentplanGetList({
          workspaceId: this.workspaceId,
          type: 'saas-cluster'
        })
        this.reset() // reset form and clear last interval
        this.reclusterShowDialog = false
        this.isLoading.recluster = false

        // Check status every 5 seconds (reclustering doesn't use websockets)
        this.interval = setInterval(async () => {
          await this.clustererStatus()
        }, 5000)
      } catch (e) {
        console.error(e)
        this.isLoading.recluster = false
      }
    },
    showReclusterDialog (contentplanId) {
      this.reclusterContentplanId = contentplanId
      this.reclusterShowDialog = true
    },
    closeReclusterDialog () {
      this.reclusterContentplanId = null
      this.reclusterShowDialog = false
    }
  },
  async mounted () {
    await this.loadData()
    document.title = 'Clusterer | ContentGecko'
  },
  beforeDestroy () {
    // Clean up interval
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  components: {
    SaaSCTA
  }
}
